// Runs only on sanitized HTML. Work on text nodes, never on HTML strings or attributes.
const BLOCKS = new Set(['P', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE']);
const LABEL = /(?:\b(?:phone(?:\s+number)?|telephone|tel|mobile|e-?mail(?:\s+address)?|address|website|web|url|fax|opening\s+hours|hours|contact|directions)\b|电话|手机|邮箱|地址|网站|网址|传真|营业时间)\s*[:：]/gi;

function* splitPhoneList(value, index) {
  const separators = [...value.matchAll(/\s*[-–—]\s*/g)].filter(separator => {
    const left = value.slice(0, separator.index).replace(/\D/g, '').length;
    const right = value.slice(separator.index + separator[0].length).replace(/\D/g, '').length;
    return left >= 7 && right >= 7;
  });
  // Prefer a visibly separated list boundary before considering a tight hyphen.
  // In "+1-212-555-1234 - 9845128", the hyphen after 555 is number formatting.
  const boundary = separators.find(separator => /\s|[–—]/.test(separator[0]))
    || separators.find(separator => value.slice(separator.index + separator[0].length).split(/[-–—]/)[0].replace(/\D/g, '').length >= 7);
  if (!boundary) {
    // Multiple possible numbers with no clear boundary: do not invent one long dial target.
    if (!separators.length) yield { value, index };
    return;
  }
  yield* splitPhoneList(value.slice(0, boundary.index), index);
  const next = boundary.index + boundary[0].length;
  yield* splitPhoneList(value.slice(next), index + next);
}

function* phoneCandidates(text) {
  // Exclude recognisable opening-time annotations from scanning, without changing
  // offsets or displayed text. A non-number barrier also prevents joining across them.
  const scanText = text.replace(/\b(?:24\s*\/\s*7|\d{1,3}\s*-?\s*(?:hours?|hrs?)|\d{1,2}(?::\d{2})?\s*(?:am|pm)|(?:[01]?\d|2[0-3]):[0-5]\d)\b/gi,
    note => '|'.repeat(note.length));
  // Parentheses are number formatting only when they enclose digits and are
  // followed by more digits (area code / optional trunk prefix). Trailing notes
  // such as "(24 hours)" and "(24/7)" are not part of the number.
  const pattern = /(?:\+?\d|\(\d{2,4}\))(?:(?:[ \u00a0.\-–—]*\d)|(?:[ \u00a0.\-–—]*\(\d{1,4}\)(?=[ \u00a0.\-–—]*\d)))*/g;
  for (const match of scanText.matchAll(pattern)) {
    // Dash spacing is not significant. Find boundaries between complete numbers,
    // while keeping short formatting groups ("+61 3 - 5555 1234") together.
    yield* splitPhoneList(match[0], match.index);
  }
}

function matches(text) {
  const found = [];
  const add = (start, value, href) => {
    const end = start + value.length;
    if (found.some(item => start < item.end && end > item.start)) return;
    found.push({ start, end, href });
  };
  const labels = [...text.matchAll(LABEL)];
  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    if (!/^(?:address|地址)/i.test(label[0])) continue;
    const start = label.index + label[0].length;
    // Pipes/semicolons separate fields, whereas commas, slashes and tight hyphens
    // are common parts of street addresses and must be preserved.
    const raw = text.slice(start, labels[i + 1]?.index ?? text.length).split(/[|;；]/)[0];
    const value = raw.trim().replace(/[\s|;；]+$/, '');
    if (value.length >= 5 && /[a-z\u3400-\u9fff]/i.test(value)) {
      add(start + raw.indexOf(value), value, `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`);
    }
  }
  for (const match of text.matchAll(/[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+/gi)) {
    add(match.index, match[0], `mailto:${encodeURIComponent(match[0]).replace(/%40/g, '@')}`);
  }
  let previousPhoneEnd = null;
  for (const match of phoneCandidates(text)) {
    const value = match.value;
    const before = text.slice(0, match.index);
    const after = text.slice(match.index + value.length);
    // Only an explicitly optional trunk prefix immediately after an international
    // country code is removed. Never strip a real zero from a local number.
    const dialValue = value.replace(/^(\+\d{1,3})\s*\(0\)/, '$1');
    const digits = dialValue.replace(/\D/g, '');
    const labeled = /(?:\b(?:phone(?:\s+number)?|telephone|tel|mobile)|电话|手机)\s*[:：]?\s*$/i.test(before);
    const continuation = previousPhoneEnd !== null && /^\s*(?:[/,;；|–—-]|or|and)\s*$/i.test(text.slice(previousPhoneEnd, match.index));
    // Bare reference numbers, dates and prices are not telephone numbers.
    if (digits.length < 7 || digits.length > 15 || /[\w@$+]$/.test(before) || /^[\w@]/.test(after)) continue;
    if (before.endsWith('/') && !continuation) continue;
    if (/^\d{4}[-.]\d{1,2}[-.]\d{1,2}$|^\d{1,2}[-.]\d{1,2}[-.]\d{4}$/.test(value)) continue;
    if (!labeled && !continuation && !value.startsWith('+') && !/^\(\d{2,4}\)/.test(value)) continue;
    if (found.some(item => match.index < item.end && match.index + value.length > item.start)) continue;
    add(match.index, value, `tel:${value.startsWith('+') ? '+' : ''}${digits}`);
    previousPhoneEnd = match.index + value.length;
  }
  return found;
}

export function linkContactText(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let nodes = [];
  const flush = () => {
    if (!nodes.length) return;
    let text = '';
    const parts = nodes.map(node => {
      const start = text.length;
      text += node.data;
      return { node, start, end: text.length };
    });
    // Reverse offsets so splitting a text node never invalidates an earlier match.
    for (const match of matches(text).sort((a, b) => b.start - a.start)) {
      for (const part of parts) {
        const start = Math.max(match.start, part.start) - part.start;
        const end = Math.min(match.end, part.end) - part.start;
        if (end <= start) continue;
        const selected = part.node.splitText(start);
        selected.splitText(end - start);
        const anchor = doc.createElement('a');
        anchor.setAttribute('href', match.href);
        anchor.className = 'dc-contact-link';
        if (match.href.startsWith('https:')) {
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
        }
        selected.replaceWith(anchor);
        anchor.appendChild(selected);
      }
    }
    nodes = [];
  };
  const walk = node => {
    if (node.nodeType === 3) {
      // A literal newline is also a contact-line boundary.
      if (node.data.includes('\n')) {
        const pieces = node.data.split('\n');
        const fragment = doc.createDocumentFragment();
        pieces.forEach((piece, index) => {
          const child = doc.createTextNode(piece);
          fragment.appendChild(child);
          nodes.push(child);
          if (index < pieces.length - 1) { flush(); fragment.appendChild(doc.createTextNode('\n')); }
        });
        node.replaceWith(fragment);
      } else nodes.push(node);
      return;
    }
    if (node.nodeType !== 1) return;
    if (node.tagName === 'A') { flush(); return; } // preserve authored links; never nest anchors
    const boundary = BLOCKS.has(node.tagName) || node.tagName === 'BR';
    if (boundary) flush();
    [...node.childNodes].forEach(walk);
    if (boundary) flush();
  };
  [...doc.body.childNodes].forEach(walk);
  flush();
  return doc.body.innerHTML;
}
